import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import BaselineQuestionnaire from './BaselineQuestionnaire';
import WheelOfLifeEvolution from '../analytics/WheelOfLifeEvolution';
import BreathingExercise from '../analytics/BreathingExercise';
import FrequencyGenerator from '../analytics/FrequencyGenerator';
import CreativeWritingTool from '../analytics/CreativeWritingTool';
import ProfileModuli from './ProfileModuli';
import FormattedNoteText from '../common/FormattedNoteText';
import RichTextEditor from '../common/RichTextEditor';

const ClientDashboard = ({ client, onLogout }) => {
    const [activeTab, setActiveTab] = useState('home');
    const [activeTool, setActiveTool] = useState('respiro'); // Nuovo stato per gli strumenti
    const [diaryEntries, setDiaryEntries] = useState([]);

    // Editor State
    const [newEntryTitle, setNewEntryTitle] = useState('');
    const [newEntry, setNewEntry] = useState('');
    const [newEntryCategory, setNewEntryCategory] = useState('');
    const [newEntryTags, setNewEntryTags] = useState(''); // Comma separated string
    const [isEntryShared, setIsEntryShared] = useState(true);
    const [editingEntryId, setEditingEntryId] = useState(null); // NEW: Track sorting editing
    const [showDiaryPreview, setShowDiaryPreview] = useState(false);

    const [showDiaryForm, setShowDiaryForm] = useState(false);
    const [meetingComment, setMeetingComment] = useState(client.nextMeetingComment || '');
    const [meetingStatus, setMeetingStatus] = useState(client.nextMeetingStatus || 'pending');

    const handleMeetingUpdate = async (status) => {
        try {
            setMeetingStatus(status);
            await DataManager.updateClient(client.id, {
                nextMeetingStatus: status,
                nextMeetingComment: meetingComment
            });
        } catch (error) {
            console.error("Error updating meeting status:", error);
        }
    };

    useEffect(() => {
        if (client && client.id) {
            loadDiaryEntries();
        }
    }, [client.id]);

    const loadDiaryEntries = async () => {
        try {
            const works = await DataManager.getClientWorks(client.id);
            const entries = works
                .filter(work => ['diary', 'journal', 'dream'].includes(work.type))
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setDiaryEntries(entries);
        } catch (error) {
            console.error("Error loading diary entries:", error);
        }
    };

    const insertMarkdown = (syntax) => {
        const textarea = document.getElementById('diaryTextarea');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = newEntry;
        const before = text.substring(0, start);
        const selection = text.substring(start, end);
        const after = text.substring(end);

        let newText = '';
        let newCursorPos = 0;

        if (syntax === 'bold') {
            newText = `${before}**${selection}**${after}`;
            newCursorPos = end + 4;
        } else if (syntax === 'italic') {
            newText = `${before}*${selection}*${after}`;
            newCursorPos = end + 2;
        } else if (syntax === 'list') {
            const prefix = before.endsWith('\n') || start === 0 ? '- ' : '\n- ';
            newText = `${before}${prefix}${selection}${after}`;
            newCursorPos = start + prefix.length;
        }

        setNewEntry(newText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleSaveDiary = async () => {
        if (!newEntry.trim() && !newEntryTitle.trim()) {
            alert('Scrivi almeno un titolo o del testo!');
            return;
        }

        try {
            const userTags = newEntryTags.split(',')
                .map(t => t.trim())
                .filter(t => t.length > 0);

            const contentPayload = {
                title: newEntryTitle.trim(),
                text: newEntry,
                userTags: userTags,
                category: newEntryCategory || 'Diario'
            };

            if (editingEntryId) {
                // UPDATE EXISTING
                await DataManager.updateWork(editingEntryId, {
                    content: contentPayload,
                    shared: isEntryShared
                });
                alert('✅ Nota aggiornata!');
            } else {
                // CREATE NEW
                await DataManager.saveWork(client.id, {
                    type: 'diary',
                    content: contentPayload,
                    shared: isEntryShared
                });
                alert('✅ Nota salvata!');
            }

            try {
                await DataManager.analyzeAndUpdateKeywords(client.id);
            } catch (kwErr) {
                console.warn("Could not analyze keywords:", kwErr);
            }

            // Reset Form
            setNewEntry('');
            setNewEntryTitle('');
            setNewEntryTags('');
            setNewEntryCategory('');
            setIsEntryShared(true);
            setEditingEntryId(null);
            setShowDiaryPreview(false);

            setShowDiaryForm(false);
            await loadDiaryEntries();

        } catch (error) {
            console.error("Error saving diary:", error);
            alert("Errore nel salvataggio: " + error.message);
        }
    };

    const handleEditEntry = (entry) => {
        // Populate form
        const content = typeof entry.content === 'object' ? entry.content : { text: entry.content };

        setNewEntryTitle(content.title || '');
        setNewEntry(content.text || (typeof entry.content === 'string' ? entry.content : ''));
        setNewEntryCategory(content.category || '');
        setNewEntryTags(content.userTags ? content.userTags.join(', ') : '');
        setIsEntryShared(entry.shared || false);

        setEditingEntryId(entry.id);
        setShowDiaryForm(true);

        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteEntry = async (entryId) => {
        if (confirm("Sei sicuro di voler eliminare questa nota?")) {
            try {
                await DataManager.deleteWork(entryId);
                await loadDiaryEntries();
            } catch (error) {
                console.error("Error deleting entry:", error);
                alert("Errore durante l'eliminazione.");
            }
        }
    };

    // Helper to render formatted text safely
    const renderFormattedText = (text) => {
        if (!text) return null;
        const lines = text.split('\n');
        return lines.map((line, idx) => {
            if (line.trim().startsWith('- ')) {
                const content = line.trim().substring(2);
                return <li key={idx} className="ml-4 list-disc">{formatInline(content)}</li>;
            }
            if (!line.trim()) return <br key={idx} />;
            return <p key={idx} className="mb-2 min-h-[1.2em]">{formatInline(line)}</p>;
        });
    };

    const formatInline = (text) => {
        const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            if (part.startsWith('*') && part.endsWith('*')) {
                return <em key={i}>{part.slice(1, -1)}</em>;
            }
            return part;
        });
    };

    const categories = ['Diario Personale', 'Lavoro', 'Relazioni', 'Sogni', 'Salute', 'Altro'];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white shadow-[0_2px_10px_-4px_rgba(0,0,0,0.1)] border-b border-gray-100 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
                    <div className="flex justify-between items-center">
                        {/* BRAND LOGO */}
                        <div className="flex items-center gap-2">
                            <img src="/logo.png" alt="AwarenessTool Logo" className="h-7 md:h-10 object-contain drop-shadow-sm" />
                            <span className="text-xs md:text-sm font-black tracking-tighter text-slate-800 uppercase">AwarenessTool</span>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                                <h1 className="text-sm font-bold text-slate-800">
                                    Ciao, {client.name?.split(' ')[0] || 'Cliente'}!
                                </h1>
                                <p className="text-xs text-slate-500 font-medium">Spazio Personale</p>
                            </div>
                            {/* Esci solo su desktop — su mobile è nella bottom nav */}
                            <button
                                onClick={onLogout}
                                className="hidden md:inline-flex px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition shadow-sm"
                            >
                                Esci
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Navbar unificata — bottom su mobile, sticky sotto header su desktop */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-100 z-40 md:static md:border-t-0 md:border-b md:border-slate-100 md:bg-white">
                <div className="flex justify-around md:justify-center md:gap-2 px-2 py-2 md:px-6 md:py-2 max-w-6xl md:mx-auto pb-safe">
                    {[
                        { id: 'home',     label: 'Home',     icon: '🧘\u200d♀️' },
                        { id: 'diary',    label: 'Diario',   icon: '📔' },
                        { id: 'profile',  label: 'Profilo',  icon: '👤' },
                        { id: 'progress', label: 'Qui ed Ora', icon: '🧭' },
                        { id: 'tools',    label: 'Strumenti', icon: '🧩' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex flex-col md:flex-row items-center gap-0.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-xl text-xs font-bold transition-all ${
                                activeTab === tab.id
                                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-100'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                            }`}
                        >
                            <span className="text-xl md:text-sm">{tab.icon}</span>
                            <span className="text-[9px] md:text-xs whitespace-nowrap">{tab.label}</span>
                        </button>
                    ))}
                    {/* Esci — solo su mobile (su desktop c'è nell'header) */}
                    <button
                        onClick={onLogout}
                        className="flex flex-col md:hidden items-center gap-0.5 px-2 py-1.5 rounded-xl text-xs font-bold text-slate-400 hover:text-red-500 transition-all"
                    >
                        <span className="text-xl">🚪</span>
                        <span className="text-[9px]">Esci</span>
                    </button>
                </div>
            </nav>

            <main className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-8 pb-28 md:pb-10">
                <div className="space-y-6">

                    {/* Home Tab - Revamped */}
                    {activeTab === 'home' && (
                        <div className="space-y-6 animate-fade-in">
                            {/* Hero Banner Terapeutico */}
                            <div className="bg-gradient-to-br from-emerald-50 via-teal-50/50 to-white rounded-3xl p-8 border border-emerald-100/50 shadow-sm relative overflow-hidden">
                                <div className="absolute right-0 top-0 opacity-5 transform translate-x-1/4 -translate-y-1/4 pointer-events-none">
                                    <span className="text-9xl">🌿</span>
                                </div>
                                <div className="relative z-10">
                                    <h2 className="text-3xl font-black text-slate-800 mb-3 tracking-tight">
                                        Bentornato nel tuo spazio sicuro.
                                    </h2>
                                    <p className="text-slate-600 text-lg max-w-2xl leading-relaxed">
                                        Questo è il tuo ambiente privato dedicato all'ascolto interiore e alla consapevolezza. Prendi un respiro profondo, esplora i tuoi pensieri sul diario o aggiorna la tua bussola emotiva.
                                    </p>
                                </div>
                            </div>

                            {/* Quick Actions Restyled */}
                            <div className="grid md:grid-cols-2 gap-6">
                                <div
                                    onClick={() => setActiveTab('diary')}
                                    className="bg-white rounded-3xl border border-slate-100 p-8 cursor-pointer hover:shadow-md hover:border-emerald-200 transition-all group flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="text-5xl mb-4 grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all origin-left">✍️</div>
                                        <h3 className="text-xl font-bold mb-2 text-slate-800 font-sans">Scrivi nel tuo Diario</h3>
                                        <p className="text-slate-500 text-sm font-sans leading-relaxed">
                                            Lascia fluire pensieri ed emozioni sulla carta. Tu decidi cosa è strettamente confidenziale e cosa condividere con il tuo counselor durante gli incontri.
                                        </p>
                                    </div>
                                    <div className="mt-6 font-bold text-emerald-600 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                                        Apri il diario <span>→</span>
                                    </div>
                                </div>

                                <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm flex flex-col justify-between relative overflow-hidden">
                                     <div className="absolute right-[-20px] bottom-[-20px] text-8xl opacity-5 pointer-events-none grayscale">🗓️</div>
                                    <div>
                                        <div className="text-5xl mb-4 grayscale opacity-80">🗓️</div>
                                        <h3 className="text-xl font-bold mb-2 text-slate-800 font-sans tracking-tight">Prossimo Incontro</h3>
                                        {client.nextMeetingDate ? (
                                            <div className="space-y-4 mt-4">
                                                <div className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 rounded-2xl p-5 border border-emerald-100 shadow-sm relative overflow-hidden group/date transition-all hover:shadow-md">
                                                     <div className="absolute top-0 right-0 p-2 opacity-10 group-hover/date:opacity-20 transition-opacity">✨</div>
                                                    <p className="text-4xl font-black mb-1 font-sans text-emerald-900 leading-none tracking-tighter">
                                                        {new Date(client.nextMeetingDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}
                                                    </p>
                                                    <p className="text-emerald-700 font-black uppercase tracking-widest text-[10px] flex items-center gap-2 mt-2">
                                                        <span className="text-sm">⏱️</span> Ore {new Date(client.nextMeetingDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>

                                                <div className="flex flex-col gap-3">
                                                    <div className="flex gap-2">
                                                        <button 
                                                            onClick={() => handleMeetingUpdate('confirmed')}
                                                            className={`flex-1 py-3 px-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 transform active:scale-95 ${meetingStatus === 'confirmed' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200 ring-4 ring-emerald-50' : 'bg-white text-emerald-600 border-2 border-emerald-100 hover:border-emerald-500 hover:bg-emerald-50'}`}
                                                        >
                                                            {meetingStatus === 'confirmed' ? '✅ Confermata' : 'Conferma'}
                                                        </button>
                                                        <button 
                                                            onClick={() => handleMeetingUpdate('reschedule')}
                                                            className={`flex-1 py-3 px-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 transform active:scale-95 ${meetingStatus === 'reschedule' ? 'bg-orange-500 text-white shadow-lg shadow-orange-200 ring-4 ring-orange-50' : 'bg-white text-orange-500 border-2 border-orange-100 hover:border-orange-500 hover:bg-orange-50'}`}
                                                        >
                                                            {meetingStatus === 'reschedule' ? '📅 Da rischedulare' : 'Rischedula'}
                                                        </button>
                                                    </div>
                                                    <div className="relative group/input">
                                                        <input 
                                                            type="text"
                                                            value={meetingComment}
                                                            onChange={(e) => setMeetingComment(e.target.value)}
                                                            onBlur={() => handleMeetingUpdate(meetingStatus)}
                                                            placeholder="Commento (es: ho bisogno di spostare...)"
                                                            className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-2xl text-xs focus:ring-0 focus:border-indigo-400 focus:bg-white outline-none transition-all duration-300 font-bold placeholder:text-slate-300"
                                                        />
                                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within/input:text-indigo-400 transition-colors pointer-events-none">
                                                            💬
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-4 bg-slate-50 rounded-2xl p-4 border border-slate-100 border-dashed">
                                                <p className="text-slate-500 font-medium text-sm font-sans flex items-center gap-2">
                                                    <span>⏳</span> Nessun incontro programmato a breve. Verrà comunicato dal tuo counselor.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Profilo & Questionari Tab */}
                    {activeTab === 'profile' && (
                        <div className="animate-fade-in">
                            <ProfileModuli client={client} />
                        </div>
                    )}

            {/* Diary Tab */}
            {activeTab === 'diary' && (
                <div className="space-y-6">
                    {/* Header + New Entry Button */}
                    <div className="bg-white rounded-3xl shadow-lg p-6">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                    <span>📔</span> Il Mio Diario
                                </h2>
                                <p className="text-gray-500 text-sm mt-1">
                                    {diaryEntries.length} {diaryEntries.length === 1 ? 'voce' : 'voci'}
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    // Reset form for new entry
                                    setNewEntryTitle('');
                                    setNewEntry('');
                                    setNewEntryCategory('');
                                    setNewEntryTags('');
                                    setIsEntryShared(true);
                                    setEditingEntryId(null);
                                    setShowDiaryForm(!showDiaryForm);
                                }}
                                className="px-6 py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition font-sans border-0"
                            >
                                {showDiaryForm && !editingEntryId ? 'Chiudi' : 'Nuova Voce'}
                            </button>
                        </div>
                    </div>

                    {/* New/Edit Entry Form */}
                    {showDiaryForm && (
                        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-4 border-2 border-brand-100">
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="text-lg font-bold text-gray-800">
                                    {editingEntryId ? '✏️ Modifica Nota' : '✍️ Nuova Nota'}
                                </h3>
                            </div>

                            {/* Title */}
                            <input
                                type="text"
                                value={newEntryTitle}
                                onChange={(e) => setNewEntryTitle(e.target.value)}
                                className="w-full p-3 text-lg font-bold border-b-2 border-gray-200 focus:border-brand-500 focus:outline-none placeholder-gray-400"
                                placeholder="Titolo (es. Riflessioni di oggi...)"
                            />

                            {/* Meta: Category & Tags */}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Categoria</label>
                                    <select
                                        value={newEntryCategory}
                                        onChange={(e) => setNewEntryCategory(e.target.value)}
                                        className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-500"
                                    >
                                        <option value="">Seleziona Categoria...</option>
                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Tag (opzionali)</label>
                                    <input
                                        type="text"
                                        value={newEntryTags}
                                        onChange={(e) => setNewEntryTags(e.target.value)}
                                        className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-500"
                                        placeholder="separati da virgola (es. ansia, lavoro)"
                                    />
                                </div>
                            </div>

                            {/* Rich Text Editor */}
                            <RichTextEditor
                                value={newEntry}
                                onChange={(val) => setNewEntry(val)}
                                placeholder="Scrivi qui i tuoi pensieri e riflessioni (i pulsanti formattano direttamente il testo)..."
                                minHeight="200px"
                            />

                            {/* Privacy Toggle */}
                            <div className="flex items-center gap-3 bg-brand-50 p-4 rounded-xl">
                                <input
                                    type="checkbox"
                                    id="shareToggle"
                                    checked={isEntryShared}
                                    onChange={(e) => setIsEntryShared(e.target.checked)}
                                    className="w-5 h-5 text-brand-600 rounded focus:ring-2 focus:ring-brand-500 cursor-pointer"
                                />
                                <label htmlFor="shareToggle" className="text-sm text-brand-900 font-medium cursor-pointer select-none">
                                    {isEntryShared ? (
                                        <>👁️ <strong>Condivisa</strong> <span className="text-brand-600/70 ml-1">- Il counselor potrà leggere questa nota</span></>
                                    ) : (
                                        <>🔒 <strong>Privata</strong> <span className="text-brand-600/70 ml-1">- Visibile solo a te</span></>
                                    )}
                                </label>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={handleSaveDiary}
                                    className="flex-1 py-3 text-white rounded-xl font-bold transition flex justify-center items-center gap-2 border-0"
                                    style={{ backgroundColor: '#24485d' }}
                                >
                                    <span>💾</span> {editingEntryId ? 'Aggiorna Nota' : 'Salva Nota'}
                                </button>
                                <button
                                    onClick={() => {
                                        setNewEntry('');
                                        setShowDiaryForm(false);
                                        setEditingEntryId(null);
                                    }}
                                    className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition"
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Diary Entries List */}
                    {diaryEntries.length === 0 ? (
                        <div className="bg-white rounded-3xl shadow-lg p-12 text-center text-gray-400">
                            <div className="text-6xl mb-4">📭</div>
                            <p className="text-lg">Il tuo diario è vuoto</p>
                            <p className="text-sm mt-2">Inizia a scrivere le tue riflessioni!</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {diaryEntries.map((entry) => {
                                // Normalizing content
                                const isComplex = typeof entry.content === 'object' && entry.content !== null;
                                const contentObj = isComplex ? entry.content : { text: entry.content };
                                const text = contentObj.text || (typeof entry.content === 'string' ? entry.content : "");
                                const title = contentObj.title;
                                const category = contentObj.category;
                                const tags = contentObj.userTags || [];

                                return (
                                    <div
                                        key={entry.id}
                                        className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:border-brand-200 transition relative"
                                    >
                                        <div className="absolute top-4 right-4 flex gap-2">
                                            <button
                                                onClick={() => handleEditEntry(entry)}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                                                title="Modifica Nota"
                                            >
                                                ✏️ Modifica
                                            </button>
                                            <button
                                                onClick={() => handleDeleteEntry(entry.id)}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-sm font-medium"
                                                title="Elimina Nota"
                                            >
                                                🗑️
                                            </button>
                                        </div>

                                        <div className="flex justify-between items-start mb-4 pr-20">
                                            <div className="space-y-1">
                                                {/* Date & Time */}
                                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                                    <span>
                                                        {new Date(entry.timestamp).toLocaleDateString('it-IT', {
                                                            day: 'numeric', month: 'short', year: 'numeric'
                                                        })}
                                                    </span>
                                                    <span>•</span>
                                                    <span>
                                                        {new Date(entry.timestamp).toLocaleTimeString('it-IT', {
                                                            hour: '2-digit', minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>

                                                {/* Title (if present) */}
                                                {title && (
                                                    <h3 className="text-xl font-bold text-gray-800">{title}</h3>
                                                )}

                                                {/* Category & Tags */}
                                                {(category || tags.length > 0) && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {category && (
                                                            <span className="bg-brand-100 text-brand-700 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide">
                                                                {category}
                                                            </span>
                                                        )}
                                                        {tags.map((tag, i) => (
                                                            <span key={i} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                                                                #{tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Access Badge */}
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${entry.shared
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-700'
                                                    }`}
                                            >
                                                {entry.shared ? '👁️ Condivisa' : '🔒 Privata'}
                                            </span>
                                        </div>

                                        {/* Content */}
                                        <div className="text-gray-700 text-sm leading-relaxed">
                                            <FormattedNoteText text={text} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Progress Tab */}
            {activeTab === 'progress' && (
                <div className="space-y-6">
                    {/* Progress Banner */}
                    <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 relative overflow-hidden">
                        <div className="relative z-10">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">✨ La tua Bussola</h3>
                            <p className="text-slate-800 italic text-lg font-sans">
                                "Da soli si va veloci, insieme si va lontano."
                            </p>
                        </div>
                        <div className="absolute right-0 bottom-0 opacity-10 transform translate-y-1/4 translate-x-1/4 grayscale">
                            <span className="text-9xl">🏔️</span>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl shadow-lg p-6">
                        <WheelOfLifeEvolution client={client} />
                    </div>
                </div>
            )}

            {/* Tools Tab */}
            {activeTab === 'tools' && (
                <div className="space-y-6 animate-fade-in">
                    {/* Tools Banner */}
                    <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 relative overflow-hidden">
                        <div className="relative z-10">
                            <h3 className="text-2xl font-bold mb-2 text-black font-sans">🧘 La tua Palestra Emotiva</h3>
                            <p className="text-slate-800 italic text-lg font-sans">
                                Strumenti per ritrovare il centro e la calma.
                            </p>
                        </div>
                        <div className="absolute right-0 bottom-0 opacity-10 transform translate-y-1/4 translate-x-1/4 grayscale">
                            <span className="text-9xl">🌬️</span>
                        </div>
                    </div>

                    {/* Tool Selection Tabs */}
                    <div className="flex gap-2 p-1 bg-gray-100 rounded-xl w-fit flex-wrap">
                        <button
                            onClick={() => setActiveTool('respiro')}
                            className={`px-6 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 ${
                                activeTool === 'respiro' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <span>🌬️</span> Respiro
                        </button>
                        <button
                            onClick={() => setActiveTool('frequenze')}
                            className={`px-6 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 ${
                                activeTool === 'frequenze' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <span>🎵</span> Frequenze
                        </button>
                        <button
                            onClick={() => setActiveTool('scrittura')}
                            className={`px-6 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 ${
                                activeTool === 'scrittura' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <span>✍️</span> Scrittura Creativa
                        </button>
                    </div>

                    {/* Active Tool Content */}
                    <div className="bg-white rounded-3xl shadow-lg p-6 flex flex-col min-h-[500px]">
                        {activeTool === 'respiro' && (
                            <div className="animate-fade-in w-full h-full">
                                <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <span>🌬️</span> Respiro Consapevole
                                </h4>
                                <BreathingExercise client={client} />
                            </div>
                        )}
                        
                        {activeTool === 'frequenze' && (
                            <div className="animate-fade-in w-full h-full">
                                <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <span>🎵</span> Frequenze di Riequilibrio
                                </h4>
                                <FrequencyGenerator client={client} />
                            </div>
                        )}
                        
                        {activeTool === 'scrittura' && (
                            <div className="animate-fade-in w-full h-full">
                                <CreativeWritingTool client={client} />
                            </div>
                        )}
                    </div>
                </div>
            )}
            </div>
            </main>
        </div>
    );
};

const ClientView = ({ user, onLogout }) => {
    const [client, setClient] = useState(user || null);
    const [loading, setLoading] = useState(!user);

    useEffect(() => {
        const fetchClientData = async () => {
            if (!user) return;
            try {
                let clientData = null;
                const targetUid = user.firebaseUid || user.id;

                if (targetUid) {
                    clientData = await DataManager.getClientByUid(targetUid);
                }
                if (!clientData && user.id) {
                    clientData = await DataManager.getClientById(user.id);
                }
                if (!clientData && user.email) {
                    clientData = await DataManager.getClientByEmail(user.email);
                }

                if (clientData) {
                    setClient(clientData);
                } else if (user) {
                    setClient(user);
                }
            } catch (err) {
                console.error("Error fetching client data in ClientView:", err);
                if (user) {
                    setClient(user);
                }
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            setClient(user);
            fetchClientData();
        }
    }, [user?.id, user?.firebaseUid, user?.email]);

    if (loading && !client) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-pulse">⏳</div>
                    <p className="text-gray-600">Caricamento...</p>
                </div>
            </div>
        );
    }

    if (!client && !user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md bg-white p-8 rounded-3xl shadow-lg border border-red-100">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Profilo non trovato</h2>
                    <p className="text-gray-600 mb-6">Il tuo account risulta associato, ma i dati del profilo sono introvabili o l'ID è stato modificato in modo errato.</p>
                    <button onClick={onLogout} className="px-6 py-2 bg-gray-800 text-white rounded-xl font-medium">Torna al Login</button>
                </div>
            </div>
        );
    }

    return (
        <ClientDashboard client={client || user} onLogout={onLogout} />
    );
};

export default ClientView;
